import { fabric } from 'fabric';
import { StaticCanvas, Line, Circle } from 'fabric/fabric-impl';
import { mapKeys, isEqual } from 'lodash';
import { IPoseData } from '@interfaces/pose.interface';

import Dependency from './dependency';

interface SkeletonRendererOptions {
  isModifiable?: boolean;
  background?: string;
  dimension?: { width: number; height: number };
  offset?: { x: number; y: number };
}

class SkeletonRenderer {
  private canvas: StaticCanvas;
  private options: SkeletonRendererOptions;
  private fabrics: Array<Line | Circle>;
  private fabricsKeys: string[];
  private initialized: boolean;
  private isSameDimensions: Record<string, unknown>;

  constructor(canvas: HTMLCanvasElement, options: SkeletonRendererOptions) {
    this.canvas = new fabric.Canvas(canvas, {
      hoverCursor: 'pointer',
      selection: false,
      targetFindTolerance: 32,
      backgroundColor: options.background || 'transparent',
    });

    this.options = { ...options };
    this.fabrics = [];
    this.fabricsKeys = [];
    this.initialized = false;
    this.isSameDimensions = { width: 128, height: 96 };
    this.options.dimension = { width: 128, height: 96 };
    this.options.offset = { x: 0.2, y: 0.2 };
    this.canvas.setDimensions(this.options.dimension);
  }

  public render(poseData: IPoseData) {
    const data = mapKeys(poseData.data.keypoints, 'part');
    if (!this.initialized || (this.initialized && this.options.isModifiable)) {
      if (this.initialized) {
        this.fabrics = [];
        this.canvas.remove(...this.canvas.getObjects());
      }
      Object.keys(data).forEach((key: string) => {
        if (
          key === 'nose' ||
          key === 'leftEye' ||
          key === 'rightEye' ||
          key === 'leftEar' ||
          key === 'rightEar'
        )
          return;

        if (typeof Dependency[key] === 'object') {
          Dependency[key].forEach((dep: string, index: number) => {
            this.fabrics.push(
              this.drawLine(
                [
                  data[key].position.x * this.options.offset!.x,
                  data[key].position.y * this.options.offset!.y,
                  data[Dependency[key][index]].position.x * this.options.offset!.x,
                  data[Dependency[key][index]].position.y * this.options.offset!.y,
                ],
                `line-${key}-${dep}`,
              ),
            );
            this.fabricsKeys.push(`line-${key}-${dep}`);
          });
        } else {
          this.fabrics.push(
            this.drawLine(
              [
                data[key].position.x * this.options.offset!.x,
                data[key].position.y * this.options.offset!.y,
                data[Dependency[key]].position.x * this.options.offset!.x,
                data[Dependency[key]].position.y * this.options.offset!.y,
              ],
              `line-${key}-${Dependency[key]}`,
            ),
          );
          this.fabricsKeys.push(`line-${key}-${Dependency[key]}`);
        }
      });

      Object.keys(data).forEach((key) => {
        if (
          key === 'nose' ||
          key === 'leftEye' ||
          key === 'rightEye' ||
          key === 'leftEar' ||
          key === 'rightEar'
        ) {
          this.fabrics.push(
            this.drawFacePoint(
              data[key].position.x * this.options.offset!.x,
              data[key].position.y * this.options.offset!.y,
              `face-${key}`,
            ),
          );
          this.fabricsKeys.push(`face-${key}`);
        } else {
          //Check the left side for draw joint
          const isLeft = key.startsWith('lef');

          this.fabrics.push(
            this.drawJoint(
              data[key].position.x * this.options.offset!.x,
              data[key].position.y * this.options.offset!.y,
              `joint-${key}`,
              this.options.isModifiable!,
              isLeft,
            ),
          );
          this.fabricsKeys.push(`joint-${key}`);
        }
      });
      this.canvas.add(...this.fabrics);
    } else {
      const fabricObjects = this.canvas.getObjects();

      Object.keys(data).forEach((key: string) => {
        if (
          key === 'nose' ||
          key === 'leftEye' ||
          key === 'rightEye' ||
          key === 'leftEar' ||
          key === 'rightEar'
        )
          return;

        if (typeof Dependency[key] === 'object') {
          Dependency[key].forEach((dep: string, index: number) => {
            const t = fabricObjects[this.fabricsKeys.indexOf(`line-${key}-${dep}`)] as fabric.Line;

            t.set({
              x1: data[key].position.x * this.options.offset!.x,
              y1: data[key].position.y * this.options.offset!.y,
              x2: data[Dependency[key][index]].position.x * this.options.offset!.x,
              y2: data[Dependency[key][index]].position.y * this.options.offset!.y,
            });
          });
        } else {
          const t = fabricObjects[
            this.fabricsKeys.indexOf(`line-${key}-${Dependency[key]}`)
          ] as fabric.Line;

          t.set({
            x1: data[key].position.x * this.options.offset!.x,
            y1: data[key].position.y * this.options.offset!.y,
            x2: data[Dependency[key]].position.x * this.options.offset!.x,
            y2: data[Dependency[key]].position.y * this.options.offset!.y,
          });
        }
      });

      Object.keys(data).forEach((key) => {
        if (
          key === 'nose' ||
          key === 'leftEye' ||
          key === 'rightEye' ||
          key === 'leftEar' ||
          key === 'rightEar'
        ) {
          const t = fabricObjects[this.fabricsKeys.indexOf(`face-${key}`)];

          t.set({
            left: data[key].position.x * this.options.offset!.x,
            top: data[key].position.y * this.options.offset!.y,
          });
        } else {
          const t = fabricObjects[this.fabricsKeys.indexOf(`joint-${key}`)];

          t.set({
            left: data[key].position.x * this.options.offset!.x,
            top: data[key].position.y * this.options.offset!.y,
          });
        }
      });
      this.canvas.renderAll();
    }

    if (!this.initialized) this.initialized = true;
  }

  public setDimensions = (dimensions: { width: number; height: number }) => {
    if (this.options.isModifiable) {
      if (!isEqual(this.isSameDimensions, dimensions)) {
        this.options.offset = {
          x: dimensions.width / 640,
          y: dimensions.height / 480,
        };
        this.isSameDimensions = dimensions;
        this.canvas.setDimensions(dimensions);
      }
    }
  };

  public track = (callback: any): void => {
    this.canvas.on('object:moving', (onChange: any) => {
      //for better ux
      this.canvas.sendToBack(onChange!.target);
      callback({
        action: 'moving',
        name: onChange!.target.name,
        movedX: onChange!.pointer.x,
        movedY: onChange!.pointer.y,
        offset: this.options.offset,
      });
    });
    // this.canvas.on({
    //   'object:moving': (onChange: any) => {
    //     //for better ux
    //     this.canvas.sendToBack(onChange!.target);
    //     callback({
    //       action: 'moving',
    //       name: onChange!.target.name,
    //       movedX: onChange!.pointer.x,
    //       movedY: onChange!.pointer.y,
    //       offset: this.options.offset,
    //     });
    //   },
    // });
  };

  private drawJoint = (
    x: number,
    y: number,
    name: string,
    modifiable: boolean,
    isLeft: boolean,
  ) => {
    return new fabric.Circle({
      name,
      left: x,
      top: y,
      strokeWidth: (1 * (this.options.offset!.x + this.options.offset!.y)) / 2,
      radius: (4 * (this.options.offset!.x + this.options.offset!.y)) / 2,
      fill: isLeft ? '#CC1937' : '#1860CC',
      stroke: '#FFF',
      selectable: modifiable ? true : false,
      hasControls: false,
      originX: 'center',
      originY: 'center',
    });
  };

  private drawFacePoint = (x: number, y: number, name: string) => {
    return new fabric.Circle({
      name,
      left: x,
      top: y,
      strokeWidth: (0.5 * (this.options.offset!.x + this.options.offset!.y)) / 2,
      radius: (2 * (this.options.offset!.x + this.options.offset!.y)) / 2,
      fill: '#fff',
      stroke: '#8100ff',
      selectable: false,
      originX: 'center',
      originY: 'center',
    });
  };

  private drawLine = (coords: number[], name: string) => {
    return new fabric.Line(coords, {
      name,
      fill: '#8100ff',
      stroke: '#8100ff',
      strokeWidth: (3 * (this.options.offset!.x + this.options.offset!.y)) / 2,
      selectable: false,
      originX: 'center',
      originY: 'center',
      hoverCursor: 'default',
    });
  };
}

export default SkeletonRenderer;
